/**
 * The benefit-cliff engine (SPEC-4 §7.4, §A1) — the marquee Pillar 4 item.
 *
 * Every calculator on this site answers "what do I owe" or "what am I owed".
 * Neither answers the question that actually decides whether a working
 * household gets ahead: **if I earn more, am I better off?** Past a threshold,
 * the answer is sometimes no. A raise crosses an eligibility line, a credit
 * phases out faster than the wage grows, and total resources fall while gross
 * income rises. That is a *cliff*, and no consumer tool shows it, because the
 * households it happens to are not a lead-generation audience.
 *
 * This module sweeps income and sums what a household actually has:
 *
 *     total resources = wages
 *                     − federal income tax − FICA − state income tax
 *                     + EITC + the Child Tax Credit it can actually use
 *                       (all of the refundable part, and the rest up to the
 *                        federal income tax it offsets)
 *                     + ACA premium tax credit
 *                     + SNAP
 *
 * It composes engines that already exist and shards that are already bundled
 * and cited, so no new data is required — only the arithmetic nobody does.
 *
 * Three honesty rules are load-bearing, not decorative:
 *
 *   1. **Medicaid is never monetized.** Losing Medicaid is the largest cliff in
 *      American benefits, and pricing a household's coverage is not something we
 *      can do from public data. It is reported as a status change at the income
 *      where it happens, never converted to dollars. A number we cannot source
 *      would make the whole chart untrustworthy.
 *   2. **The sweep is bounded.** At most {@link MAX_POINTS} evaluations, with the
 *      step widened rather than the range truncated (SPEC-3 §2.7's "iterative
 *      loops have a proven upper bound").
 *   3. **What is not modeled is reported.** Housing assistance, childcare
 *      subsidies, WIC, LIHEAP, TANF, and state-only programs are not here, and
 *      several have steeper cliffs than anything that is. The caller renders
 *      {@link SweepResult.unmodeled} so the chart never reads as complete.
 */
import {
  estimateCtc,
  estimateEitc,
  estimatePremiumTaxCredit,
  estimateSnap,
  medicaidEligibility,
  povertyLine,
} from "./benefits";
import { evaluateTaxes, type TaxContext } from "./tax";
import { bracketsFor, statutoryNotches, type StatutoryNotch } from "./tax/brackets";
import type { FilingStatus } from "../data/schemas";
import type {
  AcaData,
  EitcCtcData,
  FederalPovertyLevelData,
  MedicaidData,
  SnapData,
} from "../data/schemas";

/** Hard ceiling on sweep evaluations (SPEC-4 §7.4). */
export const MAX_POINTS = 400;
/** Default income step, and the range the step is clamped into. */
export const DEFAULT_STEP = 250;
export const MIN_STEP = 50;
export const MAX_STEP = 5_000;
/** Ceiling on the swept income range. */
export const MAX_INCOME = 250_000;
/**
 * A resource dip smaller than this is float noise from cent-level rounding
 * across five engines, not a cliff. Reporting it would cry wolf.
 */
export const CLIFF_NOISE_FLOOR = 1;

/** The household being swept. */
export interface CliffInput {
  filingStatus: FilingStatus;
  householdSize: number;
  /** Children who qualify for the EITC and the Child Tax Credit. */
  qualifyingChildren: number;
  /** Two-letter state code; "" when no state income tax is modeled. */
  stateCode: string;
  /**
   * Mandatory residence-based local taxes that apply to this household — the
   * Maryland and Indiana county taxes, which every resident pays and which come
   * straight off "what a household actually has". Up to 3.3 points of income in
   * Maryland and 3.0 in Indiana, so leaving them out shifted the whole resource
   * curve down by more than a thousand dollars a year at ordinary incomes.
   * Empty for every jurisdiction without one, and for opt-in locals, which the reader
   * chooses in the Take-Home tile rather than declaring here.
   */
  localJurisdictionIds?: string[];
  /**
   * The household's benchmark (second-lowest-cost silver) monthly premium, from
   * HealthCare.gov. Per-county, so the user supplies it; 0 opts the ACA term out.
   */
  benchmarkMonthlyPremium: number;
}

/** The datasets the sweep needs. Any may be absent; the sweep degrades. */
export interface CliffData {
  tax: TaxContext;
  fpl: FederalPovertyLevelData | null;
  eitcCtc: EitcCtcData | null;
  aca: AcaData | null;
  snap: SnapData | null;
  medicaid: MedicaidData | null;
  /**
   * SNAP allotments are bundled for the contiguous states only. Alaska and
   * Hawaii run on different tables, so the term is skipped and *said to be
   * skipped* rather than silently estimated at the wrong level.
   */
  snapRegionSupported: boolean;
}

/** One income step: what the household earns, and what it actually has. */
export interface ResourcePoint {
  grossIncome: number;
  /** Gross less federal income tax, FICA, and state income tax. */
  netAfterTax: number;
  /** EITC plus the refundable Child Tax Credit. */
  credits: number;
  /** Annual ACA premium tax credit. */
  acaPremiumCredit: number;
  /** Annual SNAP allotment (12 × the monthly estimate). */
  snapAllotment: number;
  /** The sum that matters. */
  totalResources: number;
  /** True/false in an expansion state; null where it can't be determined. */
  medicaidEligible: boolean | null;
  /**
   * State taxable income at this point, or null where no state is modeled.
   *
   * Carried because a statutory tax step is stated against TAXABLE income while
   * this sweep runs on gross, and the gap between them is the state's own
   * deduction. Deriving it here keeps {@link findTaxSteps} a pure function of
   * the points, the way {@link findCliffs} and {@link findStatusChanges} are.
   */
  stateTaxableIncome: number | null;
}

/**
 * A point where the state's own rate schedule charges a flat amount for crossing
 * a line — not an eligibility cliff at all, and the chart could not tell the
 * reader that.
 *
 * Ohio is the only one in the repo: Ohio Rev. Code §5747.02(A)(3)(c) owes
 * nothing at or below $26,050 of nonbusiness taxable income and states the band
 * above as "$332.00 plus 2.75% of the amount in excess", with 0% bands below for
 * that $332 to not be a restatement of. A single Ohio filer sweeping past it
 * sees resources fall by about $130 with no benefit involved, and every other
 * drop on this chart is a benefit. An unexplained drop on a chart whose whole
 * purpose is explaining drops is the failure mode that matters here.
 */
export interface TaxStep {
  jurisdictionName: string;
  /** The first swept income at which the step has been charged. */
  atIncome: number;
  /** The taxable income the statute names. */
  atTaxableIncome: number;
  /** The flat amount the schedule adds. */
  amount: number;
}

/** A stretch of income where earning more does not leave you better off. */
export interface Cliff {
  /**
   * `"drop"` — resources actually fell; the raise cost money.
   * `"plateau"` — resources held flat; the raise bought nothing. Both are worth
   * seeing before taking the shift (SPEC-4 §A1, "flattens or falls"), but only
   * one of them is a loss, so they are never conflated.
   */
  kind: "drop" | "plateau";
  startIncome: number;
  endIncome: number;
  /** Income spanned by the cliff. */
  width: number;
  /** Resources lost from the peak to the trough. Zero for a plateau. */
  depth: number;
}

/** Where a discrete eligibility status changes — annotated, never priced. */
export interface StatusChange {
  program: "medicaid";
  /** The first swept income at which the new status holds. */
  atIncome: number;
  from: boolean;
  to: boolean;
}

export interface SweepOptions {
  from?: number;
  to?: number;
  step?: number;
}

export interface SweepResult {
  points: ResourcePoint[];
  cliffs: Cliff[];
  statusChanges: StatusChange[];
  /** Steps in a state's own rate schedule that this range crosses. */
  taxSteps: TaxStep[];
  /** The step actually used — may be wider than requested (see §7.4). */
  step: number;
  /** True when the step was widened to stay inside {@link MAX_POINTS}. */
  stepWidened: boolean;
  /** Programs excluded from this sweep, in plain English, for the caller to render. */
  unmodeled: string[];
}

/**
 * Programs and rules this sweep does not model, named so the chart is never read
 * as complete. Several of the omitted programs have steeper cliffs than anything
 * modeled here, so the honest statement to the user is "your real cliff may be
 * larger", never "here is your cliff".
 */
const ALWAYS_UNMODELED = [
  "Housing assistance (Section 8, public housing)",
  "Childcare subsidies (CCDF)",
  "WIC",
  "LIHEAP (energy assistance)",
  "TANF (cash assistance)",
  "State-only credits and programs, including state EITCs",
  // §24(d)(1)(B)(ii), the alternative refundable formula for three or more
  // qualifying children, is the one piece of the refundable CTC still unmodeled.
  // It can only raise the refundable amount, so the curve is a floor for those
  // households rather than reading high, which is the safe direction here.
  "The §24(d)(1)(B)(ii) alternative refundable Child Tax Credit for three or more children — it can only raise the figure, so this curve is a floor for those households",
];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** A finite, non-negative number, or the fallback. Keeps NaN off the screen. */
function finite(value: number, fallback = 0): number {
  return Number.isFinite(value) ? value : fallback;
}

/**
 * Choose the swept range and a step that keeps the point count at or under
 * {@link MAX_POINTS}. The range is preserved and the step widened, never the
 * reverse: a truncated range would silently hide every cliff above the cut,
 * while a coarser step costs resolution rather than reach.
 *
 * "A coarser step only blurs it" is what this said, and that was wrong. Cliffs
 * are found by comparing CONSECUTIVE points, so a cliff narrower than one step
 * does not blur — it disappears. Ohio's $332 charge at $26,050 is plainly
 * visible at a $250 step and gone at $500, because the ordinary gain over the
 * wider raise outruns the loss, and a large household's range widens the step
 * past $500 on its own. Widening is still the right trade, since the alternative
 * loses whole ranges rather than narrow cliffs inside them — but the caller is
 * told when it happened AND what it costs (`stepWidened`), rather than being
 * left with "no cliff found" from a sweep that could not have seen one.
 */
/**
 * How far up the income axis the sweep plots when the caller does not say.
 *
 * Four times the poverty line covers the benefit cliffs for the household size,
 * and the floor keeps a one-person household's chart from stopping below the
 * incomes people actually ask about. Both are choices this site made about a
 * chart, not figures anybody legislates — which is exactly why they are named:
 * a bound and a statutory figure look identical as inline literals.
 */
const SWEEP_FLOOR_TO = 60_000;
/** The same ceiling with no poverty-line data to scale against. */
const SWEEP_DEFAULT_TO = 100_000;

export function planSweep(
  input: CliffInput,
  data: CliffData,
  opts: SweepOptions = {},
): { from: number; to: number; step: number; stepWidened: boolean } {
  const from = clamp(finite(opts.from ?? 0), 0, MAX_INCOME);
  const defaultTo = data.fpl
    ? Math.max(4 * finite(povertyLine(input.householdSize, data.fpl).toNumber()), SWEEP_FLOOR_TO)
    : SWEEP_DEFAULT_TO;
  const to = clamp(finite(opts.to ?? defaultTo, defaultTo), from + MIN_STEP, MAX_INCOME);

  const requested = clamp(finite(opts.step ?? DEFAULT_STEP, DEFAULT_STEP), MIN_STEP, MAX_STEP);
  const span = to - from;
  const needed = Math.ceil(span / requested) + 1;
  if (needed <= MAX_POINTS) return { from, to, step: requested, stepWidened: false };

  // Widen just enough to fit, rounded up to a whole dollar.
  const widened = Math.min(MAX_STEP, Math.ceil(span / (MAX_POINTS - 1)));
  return { from, to, step: widened, stepWidened: true };
}

/** Compute one income point. Pure, and every field is guaranteed finite. */
export function resourcesAt(income: number, input: CliffInput, data: CliffData): ResourcePoint {
  const gross = Math.max(0, finite(income));
  // Only a joint return, for the credits below. §32(b)(2)(B)'s marriage-penalty
  // increase and §24(b)(2)'s $400,000 both say "in the case of a joint return",
  // and a qualifying surviving spouse files at joint RATES without filing a
  // joint return — the call `deductions.ts` already made for §170(p), and the
  // one every Pillar 2 tile makes through `marriedDefault`. The tax computation
  // above takes `filingStatus` itself, so the joint rates are unaffected.
  const married = input.filingStatus === "married_jointly";

  const tax = evaluateTaxes(
    {
      filingStatus: input.filingStatus,
      wages: gross,
      localJurisdictionIds: input.localJurisdictionIds,
    },
    data.tax,
  );
  const netAfterTax = finite(tax.totals.takeHome.toNumber());

  let credits = 0;
  if (data.eitcCtc) {
    const eitc = estimateEitc(
      { earnedIncome: gross, qualifyingChildren: input.qualifyingChildren, married },
      data.eitcCtc,
    );
    const ctc = estimateCtc(
      {
        qualifyingChildren: input.qualifyingChildren,
        magi: gross,
        married,
        // The sweep's income is wages, which is what §24(d)(1)(B)(i) measures.
        earnedIncome: gross,
      },
      data.eitcCtc,
    );
    // The NONREFUNDABLE part of the Child Tax Credit is money the household
    // keeps too — it is tax they do not pay — and this counted only the
    // refundable part, so a family with children was shown resources short by
    // at least $500 a child ($2,200 against a $1,700 refundable cap). It also
    // moved the §24 phase-out on the chart: the visible bend started where
    // `refundable` began to fall rather than at the $200,000 threshold where
    // the credit does.
    //
    // Capped at the federal income tax computed above, because a nonrefundable
    // credit cannot pay out — the refundable part is the part that can, and it
    // is already counted, so the two never overlap.
    const nonRefundable = Math.max(0, ctc.credit.toNumber() - ctc.refundable.toNumber());
    const usedAgainstTax = Math.min(nonRefundable, finite(tax.federal.incomeTax.toNumber()));
    credits =
      finite(eitc.credit.toNumber()) + finite(ctc.refundable.toNumber()) + finite(usedAgainstTax);
  }

  let acaPremiumCredit = 0;
  if (data.aca && data.fpl && input.benchmarkMonthlyPremium > 0) {
    const aca = estimatePremiumTaxCredit(
      {
        householdSize: input.householdSize,
        annualIncome: gross,
        benchmarkMonthlyPremium: input.benchmarkMonthlyPremium,
      },
      data.aca,
      data.fpl,
    );
    // Below 100% FPL there is no premium tax credit at all — that household is
    // in Medicaid territory (expansion states) or the coverage gap (the rest).
    //
    // This gate used to be load-bearing: `annualCredit` was computed for display
    // whatever the band, so without it the sweep invented thousands of dollars
    // at the low end and flattened the real step at the 100%-FPL line. It is not
    // load-bearing now. `estimatePremiumTaxCredit` bars both ends itself — the
    // §36B(c)(1)(A) floor as well as the 400% cap — and returns zero, which
    // `statutoryThresholds.test.ts` pins at each line. So this restates the
    // engine's guarantee rather than repairing its absence, and is kept as one
    // line of belt-and-braces rather than a coupling to remember. The comment is
    // updated because the old one said the engine still hands back a credit for
    // a household that cannot have one, and a reader who believed that would go
    // add this gate somewhere it is not needed.
    acaPremiumCredit = aca.eligible ? finite(aca.annualCredit.toNumber()) : 0;
  }

  let snapAllotment = 0;
  if (data.snap && data.fpl && data.snapRegionSupported) {
    const snap = estimateSnap(
      {
        householdSize: input.householdSize,
        monthlyGrossIncome: gross / 12,
        // This sweep's income is wages — it is a curve of what one more dollar
        // of EARNINGS does — so all of it is earned, and 7 CFR §273.9(d)(2)'s
        // 20% deduction applies to all of it.
        monthlyEarnedIncome: gross / 12,
      },
      data.snap,
      data.fpl,
    );
    snapAllotment = finite(snap.monthlyBenefit.toNumber()) * 12;
  }

  let medicaidEligible: boolean | null = null;
  if (data.medicaid && data.fpl && input.stateCode) {
    medicaidEligible = medicaidEligibility(
      { stateCode: input.stateCode, income: gross, householdSize: input.householdSize },
      data.medicaid,
      data.fpl,
    ).eligible;
  }

  const totalResources = finite(netAfterTax + credits + acaPremiumCredit + snapAllotment);

  return {
    grossIncome: gross,
    netAfterTax,
    credits,
    acaPremiumCredit,
    snapAllotment,
    totalResources,
    medicaidEligible,
    stateTaxableIncome: tax.state ? finite(tax.state.taxableIncome.toNumber()) : null,
  };
}

/**
 * Find every stretch where total resources do not rise as income does.
 *
 * A cliff is a maximal contiguous run of non-increasing `totalResources`. A run
 * that loses at least {@link CLIFF_NOISE_FLOOR} is a `"drop"` — the raise cost
 * money. A run that neither rises nor meaningfully falls is a `"plateau"` — the
 * raise bought nothing, which is not a loss but is exactly what a household
 * deserves to know before taking the shift. Anything shallower than the floor is
 * cent-level rounding across five engines, not a finding.
 */
export function findCliffs(points: ResourcePoint[]): Cliff[] {
  const cliffs: Cliff[] = [];
  let i = 0;
  while (i < points.length - 1) {
    if (points[i + 1]!.totalResources > points[i]!.totalResources) {
      i++;
      continue;
    }
    // Extend while resources keep failing to rise.
    let j = i;
    while (j < points.length - 1 && points[j + 1]!.totalResources <= points[j]!.totalResources) {
      j++;
    }
    const peak = points[i]!;
    const trough = points[j]!;
    const depth = peak.totalResources - trough.totalResources;
    cliffs.push({
      kind: depth >= CLIFF_NOISE_FLOOR ? "drop" : "plateau",
      startIncome: peak.grossIncome,
      endIncome: trough.grossIncome,
      width: trough.grossIncome - peak.grossIncome,
      depth: depth >= CLIFF_NOISE_FLOOR ? depth : 0,
    });
    i = j;
  }
  return cliffs;
}

/** Discrete eligibility flips across the sweep — reported, never priced. */
export function findStatusChanges(points: ResourcePoint[]): StatusChange[] {
  const changes: StatusChange[] = [];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!.medicaidEligible;
    const now = points[i]!.medicaidEligible;
    if (prev !== null && now !== null && prev !== now) {
      changes.push({
        program: "medicaid",
        atIncome: points[i]!.grossIncome,
        from: prev,
        to: now,
      });
    }
  }
  return changes;
}

/**
 * Where the swept income crosses a step in the state's own schedule.
 *
 * Reported the way a Medicaid flip is: annotated at the income where it lands,
 * never folded into the cliff it causes. A reader looking at a $130 drop is owed
 * the difference between "a benefit ended" and "the state charges $332 here".
 */
export function findTaxSteps(
  points: ResourcePoint[],
  jurisdictionName: string,
  notches: readonly StatutoryNotch[],
): TaxStep[] {
  const steps: TaxStep[] = [];
  for (const notch of notches) {
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1]!.stateTaxableIncome;
      const now = points[i]!.stateTaxableIncome;
      if (prev === null || now === null) continue;
      if (prev <= notch.taxableIncome && now > notch.taxableIncome) {
        steps.push({
          jurisdictionName,
          atIncome: points[i]!.grossIncome,
          atTaxableIncome: notch.taxableIncome,
          amount: notch.amount,
        });
        break;
      }
    }
  }
  return steps;
}

/** Sweep income and report the cliffs, the status flips, and what was left out. */
export function sweepResources(
  input: CliffInput,
  data: CliffData,
  opts: SweepOptions = {},
): SweepResult {
  const { from, to, step, stepWidened } = planSweep(input, data, opts);

  const points: ResourcePoint[] = [];
  for (let income = from; income <= to && points.length < MAX_POINTS; income += step) {
    points.push(resourcesAt(income, input, data));
  }

  const unmodeled = [...ALWAYS_UNMODELED];
  if (!data.snapRegionSupported) {
    unmodeled.unshift(
      "SNAP (Alaska and Hawaii use different allotment tables than the lower 48, which we haven't bundled)",
    );
  }
  if (input.benchmarkMonthlyPremium <= 0) {
    unmodeled.unshift(
      "The ACA premium tax credit (enter your benchmark silver premium from HealthCare.gov to include it)",
    );
  }

  return {
    points,
    cliffs: findCliffs(points),
    statusChanges: findStatusChanges(points),
    taxSteps: data.tax.state
      ? findTaxSteps(
          points,
          data.tax.state.name,
          statutoryNotches(bracketsFor(data.tax.state, input.filingStatus)),
        )
      : [],
    step,
    stepWidened,
    unmodeled,
  };
}

/**
 * The combined marginal rate on the next `delta` of income, **including benefit
 * phase-outs** (§A2, the Marginal Reality Rate). This is the sweep evaluated at
 * two adjacent points rather than across a range.
 *
 * The result can exceed 100%, and is deliberately not clamped: a household that
 * keeps none of its next $1,000 needs to see that, not a tidied-up number.
 */
export interface MarginalReality {
  fromIncome: number;
  toIncome: number;
  /** Change in take-home after tax only. */
  taxDelta: number;
  /** Change in credits + ACA + SNAP (negative when benefits phase out). */
  benefitDelta: number;
  /** Change in total resources. Negative means the raise cost money. */
  netDelta: number;
  /** Share of the raise lost to tax and lost benefits. 1.2 means 120%. */
  combinedRate: number;
  /** True when the household ends up with less than it started with. */
  netNegative: boolean;
  /** Set when Medicaid eligibility flips across this step — never priced. */
  medicaidFlip: StatusChange | null;
}

export function marginalReality(
  income: number,
  delta: number,
  input: CliffInput,
  data: CliffData,
): MarginalReality {
  const step = Math.max(1, finite(delta, 1000));
  const before = resourcesAt(income, input, data);
  const after = resourcesAt(income + step, input, data);

  const taxDelta = after.netAfterTax - before.netAfterTax;
  const benefitDelta =
    after.credits +
    after.acaPremiumCredit +
    after.snapAllotment -
    (before.credits + before.acaPremiumCredit + before.snapAllotment);
  const netDelta = after.totalResources - before.totalResources;

  const flips =
    before.medicaidEligible !== null &&
    after.medicaidEligible !== null &&
    before.medicaidEligible !== after.medicaidEligible;

  return {
    fromIncome: before.grossIncome,
    toIncome: after.grossIncome,
    taxDelta,
    benefitDelta,
    netDelta,
    combinedRate: finite((step - netDelta) / step),
    netNegative: netDelta < 0,
    medicaidFlip: flips
      ? {
          program: "medicaid",
          atIncome: after.grossIncome,
          from: before.medicaidEligible!,
          to: after.medicaidEligible!,
        }
      : null,
  };
}
